import { ExpandMoreOutlined as ExpandMoreOutlinedIcon } from '@mui/icons-material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary, { accordionSummaryClasses as summaryClasses } from '@mui/material/AccordionSummary';
import type { SxProps } from '@mui/material/styles';
import { styled } from '@mui/system';
import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import { useState } from 'react';

import { SpacingMultiple } from '../constants';
import { ContentWrapper } from '../containers/ContentWrapper';

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    borderBottom: '1px solid transparent',
    paddingInline: theme.spacing(SpacingMultiple.large),
    [`&, &.${summaryClasses.root}.${summaryClasses.expanded}`]: { minHeight: 52 },
    [`.${summaryClasses.content}.${summaryClasses.expanded}`]: { margin: 0 },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    padding: theme.spacing(SpacingMultiple.large),
}));

type CollapsibleProps = PropsWithChildren<{
    readonly ariaLabel?: string;
    readonly isInitiallyExpanded?: boolean | undefined;
    readonly summary: ReactNode;
}>;

export const Collapsible: FunctionComponent<CollapsibleProps> = ({
    ariaLabel,
    children,
    isInitiallyExpanded,
    summary,
}) => {
    const [isExpanded, setIsExpanded] = useState<boolean>(isInitiallyExpanded ?? false);

    const expandedStyled: Partial<SxProps> = isExpanded ? { borderBottomColor: 'cardborder.main' } : {};

    return (
        <ContentWrapper>
            <Accordion
                aria-label={ariaLabel}
                elevation={0}
                expanded={isExpanded}
                onChange={(_, expanded) => setIsExpanded(expanded)}
            >
                <StyledAccordionSummary expandIcon={<ExpandMoreOutlinedIcon />} sx={{ ...expandedStyled }}>
                    {summary}
                </StyledAccordionSummary>
                <StyledAccordionDetails>{children}</StyledAccordionDetails>
            </Accordion>
        </ContentWrapper>
    );
};
